/** Chamadas das Memórias. A API assina; o bucket recebe. */

export interface PutAssinado {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
}

export interface PedidoDeEnvio {
  id: string;
  uploads: { optimized: PutAssinado; preview: PutAssinado; thumb: PutAssinado };
  expiresInSeconds: number;
}

export interface Foto {
  id: string;
  dayId: string;
  activityId: string | null;
  activityTitle: string | null;
  uploader: { userId: string; memberId: string; name: string; me: boolean };
  mimeType: string;
  bytes: number;
  width: number | null;
  height: number | null;
  takenAt: string | null;
  createdAt: string;
  thumbUrl: string;
  previewUrl: string;
  canDelete: boolean;
  canReassign: boolean;
  isActivityCover: boolean;
}

export interface ResumoDoDia {
  dayId: string;
  date: string;
  title: string | null;
  photos: number;
  thumbUrls: string[];
  uploaders: string[];
}

export class MediaApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'MediaApiError';
  }
}

async function pedir<T>(url: string, init?: RequestInit): Promise<T> {
  // Content-Type só com corpo: o Fastify recusa JSON vazio (400) — e um
  // DELETE não tem corpo.
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const corpo = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new MediaApiError(res.status, corpo.error ?? 'erro', corpo.message ?? `Falha (${res.status})`);
  }
  return (await res.json()) as T;
}

export const memoriasApi = {
  criarEnvio: (tripId: string, input: { id: string; itineraryDayId: string; activityId?: string | null; mimeType: string; bytes: number; width: number; height: number; takenAt?: string | null }) =>
    pedir<PedidoDeEnvio>(`/api/v1/trips/${tripId}/uploads`, { method: 'POST', body: JSON.stringify(input) }),
  concluirEnvio: (tripId: string, id: string) =>
    pedir<{ id: string }>(`/api/v1/trips/${tripId}/uploads/${id}/complete`, { method: 'POST', body: '{}' }),
  listar: (tripId: string, dayId?: string, opcoes: { limit?: number; before?: string } = {}) => {
    const q = new URLSearchParams();
    if (dayId) q.set('dayId', dayId);
    if (opcoes.limit) q.set('limit', String(opcoes.limit));
    if (opcoes.before) q.set('before', opcoes.before);
    const qs = q.toString();
    return pedir<{ items: Foto[]; nextBefore: string | null }>(`/api/v1/trips/${tripId}/media${qs ? `?${qs}` : ''}`);
  },
  resumo: (tripId: string) => pedir<{ total: number; days: ResumoDoDia[] }>(`/api/v1/trips/${tripId}/media/summary`),
  ler: (tripId: string, id: string) => pedir<Foto & { originalUrl: string; downloadUrl: string }>(`/api/v1/trips/${tripId}/media/${id}`),
  excluir: (tripId: string, id: string) => pedir<{ ok: true }>(`/api/v1/trips/${tripId}/media/${id}`, { method: 'DELETE' }),
  /** Vincular depois: muda a atividade da foto (nulo = foto do dia). */
  trocarAtividade: (tripId: string, id: string, activityId: string | null) =>
    pedir<{ id: string; activityId: string | null; activityTitle: string | null }>(`/api/v1/trips/${tripId}/media/${id}`, { method: 'PATCH', body: JSON.stringify({ activityId }) }),
  /** Atividades do dia com versão — para a capa, que é um patch de atividade. */
  atividadesDoDia: async (dayId: string) => {
    const corpo = await pedir<{ activities: { id: string; title: string; startTime: string; version: number; coverMediaId?: string | null; deletedAt: string | null }[] }>(`/api/v1/days/${dayId}`);
    return corpo.activities.filter((a) => !a.deletedAt);
  },
  /** Capa: patch da atividade com `coverMediaId` (nulo tira). */
  definirCapa: (tripId: string, activityId: string, baseVersion: number, coverMediaId: string | null, idempotencyKey: string) =>
    pedir<{ id: string; version: number }>(`/api/v1/trips/${tripId}/activities/${activityId}`, { method: 'PATCH', headers: { 'idempotency-key': idempotencyKey }, body: JSON.stringify({ baseVersion, coverMediaId }) }),
};

/** O PUT direto ao bucket: os cabeçalhos assinados vão iguais, e mais nenhum. */
export async function enviarVariante(put: PutAssinado, blob: Blob): Promise<void> {
  const res = await fetch(put.url, { method: 'PUT', headers: put.headers, body: blob });
  if (!res.ok) throw new Error(`O armazenamento recusou o envio (${res.status}).`);
}
